import { Button, Form, Popover, Switch } from 'antd';
import { useEffect, useState } from 'react';
import StatusButton from '../../../../../../../components/statusButton';
import { ICountryExchangeDetail, IProviderDetails } from '../../../../../../../types/countries';
import PricingInputField from '../PricingInputField/PricingInputField';
import { NudosMaskedNumberInput } from '../../../../../../../components/NudosComponents';
import { roundUpNumber } from '../../../../../../../utils/formatNumbers';
import './PricingDataByCountry.scss';
import NudosDropdownWithSearchbar from '../../../../../../../components/NudosComponents/NudosDropdownWithSearchbar/NudosDropdownWithSearchbar';

export interface ItaxationRelatedDataRequiredForBilling {
  id?: number | null;
  code?: string;
  name?: string;
}

interface IpricingDataByCountryProps {
  currencyCode: string;
  flag?: string;
  countryName: string;
  countryNameCode: string;
  preloadedExchangeRate?: number;
  preloadedTaxRate?: number;
  preloadedStatus: string;
  preloadedProviderPrice?: number | null;
  preloadedMargin?: number | null;
  preloadedNudosPriceUsdWithTaxes?: number | null;
  preloadedNudosPriceUsd?: number | null;
  id?: number;
  setVerifyPricing?: (areVerified: boolean) => void;
  setChangePricing?: (change: boolean) => void;
  countriesOpenForSale?: ICountryExchangeDetail[];
  availableProviders?: IProviderDetails[];
  providerId?: number | null;
  pricingisActive?: boolean;
}

const PricingDataByCountry = (props: IpricingDataByCountryProps) => {
  const {
    currencyCode,
    flag,
    countryName,
    countryNameCode,
    preloadedExchangeRate,
    preloadedTaxRate,
    pricingisActive,
    preloadedStatus,
    preloadedProviderPrice,
    preloadedMargin,
    preloadedNudosPriceUsdWithTaxes,
    preloadedNudosPriceUsd,
    id,
    setVerifyPricing,
    setChangePricing,
    availableProviders,
    providerId
  } = props;
  const [activeCheck, setActiveCheck] = useState<boolean>(false);
  const [showActiveStatusPopover, setShowActiveStatusPopover] = useState(false);
  const { setFieldsValue } = Form.useFormInstance();
  const providerPrice = Form.useWatch(`${countryNameCode}-providerPrice`);
  const margin = Form.useWatch(`${countryNameCode}-margin`);
  const exchangeRate = Form.useWatch(`${countryNameCode}-exchangeRate`);
  const taxRate = Form.useWatch(`${countryNameCode}-taxRate`);
  const nudosPriceUsd = Form.useWatch(`${countryNameCode}-nudosPriceUsd`);
  const nudosPriceUsdWithTaxes = Form.useWatch(`${countryNameCode}-nudosPriceUsdWithTaxes`);
  const nudosProviderId = Form.useWatch(`${countryNameCode}-providerId`);
  const [newProvider, setNewProvider] = useState<IProviderDetails>();
  const isMarginValid = !margin || Math.abs(margin) < 100;
  const isProviderPriceValid = !providerPrice || +providerPrice > 0;
  const isNudosPriceUsdWithTaxesInputValid = !nudosPriceUsdWithTaxes || +nudosPriceUsdWithTaxes > 0;
  const negativeOrZeroMarginAlert = !isMarginValid || (margin <= 0 && nudosPriceUsdWithTaxes) ? ' ' : undefined;
  const productHasFullPricingDataForCountry =
    !!providerPrice && !!margin && !!exchangeRate && !!taxRate && !!nudosPriceUsdWithTaxes;
  const emptyFieldPlaceholder = '$0 USD';

  const pricingFields = [
    {
      componentSize: 'extraSmall',
      dataName: `${countryNameCode}-taxRate`,
      label: 'Impuestos',
      initialValue: preloadedTaxRate,
      currentValue: taxRate,
      suffixSymbol: '%',
      placeholder: '%',
      isRequired: false,
      isDisabled: true
    },
    {
      componentSize: 'extraSmall',
      dataName: `${countryNameCode}-exchangeRate`,
      label: 'Tasa de cambio',
      initialValue: preloadedExchangeRate,
      currentValue: exchangeRate,
      suffixSymbol: currencyCode,
      placeholder: `${currencyCode}/USD`,
      isRequired: false,
      isDisabled: true
    }
  ];

  const handleClickSwitchActiveStatus = () => {
    if (!productHasFullPricingDataForCountry) return;
    setShowActiveStatusPopover(true);
  };

  const changeActiveStatusSwith = () => {
    setActiveCheck(!activeCheck);
    setChangePricing && setChangePricing(true);
    setShowActiveStatusPopover(false);
  };

  const confirmChangeActiveStatusPopoverContent = (
    <div className="confirmChangeActiveStatusPopoverContent">
      <div className="textPopup">¿Seguro quieres {activeCheck ? 'apagar' : 'prender'} la herramienta en este país?</div>
      <Button className="buttonPoup redButton" onClick={changeActiveStatusSwith}>
        Si
      </Button>
      <Button className="buttonPoup" onClick={() => setShowActiveStatusPopover(false)}>
        No
      </Button>
    </div>
  );

  const getEcommercePriceSelectLabel = (includesTax: boolean) => {
    if (includesTax) {
      return <label className="ecommercePriceLabel labelSize">Precio tienda con impuestos</label>;
    }
    return <label className="ecommercePriceLabel labelSize">Precio sin impues.</label>;
  };

  const calculateMarginProportion = (marginParam?: number) => {
    if (typeof marginParam !== 'number' || Math.abs(marginParam) >= 100) return;
    const getMarginProportion = marginParam / 100;
    const calculatedMarginFactor = 1 - getMarginProportion;
    return calculatedMarginFactor;
  };

  const calculateSellingPriceField = (margin: number, includesTax: boolean) => {
    const calculatedMargin = calculateMarginProportion(margin);
    const buyingTaxFactor = +taxRate / 100 + 1;
    const subtotal = calculatedMargin ? +providerPrice / calculatedMargin : 0;
    if (isNaN(subtotal)) return 0;
    const usdTotalPreTax = +(subtotal / exchangeRate);
    const usdTotalPlusTax = +(subtotal / exchangeRate) * buyingTaxFactor;
    if (!includesTax) return usdTotalPreTax;
    if (includesTax) return usdTotalPlusTax;
  };

  const calculateMarginField = (nudosPriceUsdWithTaxes: number) => {
    // This formula is obtained solving for the margin in the one used to obtain the selling price
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const providerPriceTimesTaxes = +providerPrice ? providerPrice * taxFactor : 0;
    const exchangeTimesTotal = +exchangeRate && +nudosPriceUsdWithTaxes ? exchangeRate * nudosPriceUsdWithTaxes : 0;
    const providerPriceToSellingPriceProportion = exchangeTimesTotal ? providerPriceTimesTaxes / exchangeTimesTotal : 1;
    const calculatedMargin = 1 - providerPriceToSellingPriceProportion;
    return calculatedMargin * 100;
  };

  const handleChangeMargin = (newMarginInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-margin`]: newMarginInput
    });
    if (newMarginInput?.length < 1) {
      // To diferenciate the behaviour between a "0" margin and the empty string
      setFieldsValue({
        [`${countryNameCode}-nudosPrice`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsd`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceWithTaxes`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsdWithTaxes`]: ''
      });
      return;
    }
    const newMargin = isNaN(+newMarginInput) ? 0 : +newMarginInput;
    const newSellingPricePreTax = calculateSellingPriceField(newMargin, false);
    const newSellingPriceAfterTax = calculateSellingPriceField(newMargin, true);
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsd`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax, 2)}` : ''
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPrice`]: newSellingPricePreTax
        ? `${roundUpNumber(newSellingPricePreTax * +exchangeRate, 2)}`
        : ''
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax, 2)}`
        : ''
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax * +exchangeRate, 2)}`
        : ''
    });
  };

  const handleChangeNudosPriceUsdWithTaxes = (nudosPriceUsdWithTaxesInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: nudosPriceUsdWithTaxesInput
    });
    if (nudosPriceUsdWithTaxesInput?.length < 1) {
      // To diferenciate the behaviour between a "0" nudosPriceUsdWithTaxesInput and the empty string
      setFieldsValue({
        [`${countryNameCode}-nudosPriceWithTaxes`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsd`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPrice`]: ''
      });
      setFieldsValue({
        [`${countryNameCode}-margin`]: ''
      });
      return;
    }
    const sellingPriceNumber = nudosPriceUsdWithTaxesInput ? +nudosPriceUsdWithTaxesInput : 0;
    const calculatedMargin = calculateMarginField(sellingPriceNumber);
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const newPricePreTax = sellingPriceNumber ? sellingPriceNumber / taxFactor : 0;
    setFieldsValue({
      [`${countryNameCode}-nudosPriceWithTaxes`]: sellingPriceNumber
        ? `${roundUpNumber(sellingPriceNumber * +exchangeRate, 2)}`
        : ''
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsd`]: newPricePreTax ? `${roundUpNumber(newPricePreTax, 2)}` : ''
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPrice`]:
        newPricePreTax && exchangeRate ? `${roundUpNumber(newPricePreTax * +exchangeRate, 2)}` : ''
    });
    setFieldsValue({
      [`${countryNameCode}-margin`]: `${roundUpNumber(calculatedMargin, 4)}`
    });
  };

  const handleChangeProviderPrice = (providerPriceInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-providerPrice`]: providerPriceInput
    });
    handleChangeMargin(margin);
  };

  const filterBySearchStringCallback = (search: string, rawOptions: IProviderDetails[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const formTaxDataDropdownOptionsFn = (
    setCurrentOption: React.Dispatch<React.SetStateAction<IProviderDetails | undefined>>
  ) => {
    return (rawOptions: IProviderDetails[], clickOptionCallback: (option: IProviderDetails) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setFieldsValue({
            [`${countryNameCode}-providerId`]: option.id || null
          });
          setCurrentOption(option);
          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const defaultProvider = () => {
    if (providerId && availableProviders && availableProviders.length > 0) {
      const initialProvider = availableProviders.find(element => element.id === providerId);
      if (initialProvider) {
        setNewProvider(initialProvider);
        setFieldsValue({
          [`${countryNameCode}-providerId`]: initialProvider?.id || null
        });
      } else {
        setFieldsValue({
          [`${countryNameCode}-providerId`]: null
        });
      }
    } else {
      setFieldsValue({
        [`${countryNameCode}-providerId`]: null
      });
    }
  };

  useEffect(() => {
    const pricingValuesAreValid = isMarginValid && isProviderPriceValid && isNudosPriceUsdWithTaxesInputValid;
    const hasRequiredFieldsByActiveStatus = activeCheck
      ? !!margin && !!providerPrice && !!nudosPriceUsdWithTaxes && pricingValuesAreValid
      : true;
    const isVerified =
      hasRequiredFieldsByActiveStatus &&
      pricingValuesAreValid &&
      typeof taxRate === 'number' &&
      typeof exchangeRate === 'number';
    setVerifyPricing && setVerifyPricing(isVerified);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    margin,
    providerPrice,
    taxRate,
    exchangeRate,
    activeCheck,
    setVerifyPricing,
    preloadedNudosPriceUsdWithTaxes,
    nudosProviderId
  ]);

  useEffect(() => {
    const hasChanged =
      margin !== preloadedMargin ||
      preloadedExchangeRate !== exchangeRate ||
      !!pricingisActive !== activeCheck ||
      preloadedProviderPrice !== providerPrice ||
      preloadedTaxRate !== taxRate ||
      preloadedNudosPriceUsdWithTaxes !== nudosPriceUsdWithTaxes ||
      nudosProviderId !== providerId;
    setChangePricing && setChangePricing(hasChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    margin,
    providerPrice,
    taxRate,
    exchangeRate,
    activeCheck,
    setChangePricing,
    nudosPriceUsdWithTaxes,
    nudosProviderId
  ]);

  useEffect(() => {
    if (productHasFullPricingDataForCountry) return;
    setActiveCheck(false);
  }, [productHasFullPricingDataForCountry]);

  useEffect(() => {
    setActiveCheck(pricingisActive ? true : false);
  }, [pricingisActive]);

  useEffect(() => {
    setFieldsValue({
      [`${countryNameCode}-isActive`]: activeCheck
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryNameCode, activeCheck]);

  useEffect(() => {
    setFieldsValue({
      [`${countryNameCode}-id`]: id,
      [`${countryNameCode}-name`]: countryName,
      [`${countryNameCode}-flag`]: flag,
      [`${countryNameCode}-inStock`]: preloadedStatus === 'Sin stock' ? 0 : 1
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countryName, flag, preloadedStatus]);

  useEffect(() => {
    defaultProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    setFieldsValue({
      [`${countryNameCode}-providerPrice`]: preloadedProviderPrice,
      [`${countryNameCode}-margin`]: preloadedMargin,
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: preloadedNudosPriceUsdWithTaxes,
      [`${countryNameCode}-nudosPriceUsd`]: preloadedNudosPriceUsd,
      [`${countryNameCode}-nudosPrice`]: 0,
      [`${countryNameCode}-nudosPriceWithTaxes`]: 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pricingDataByCountry">
      <div className="countryInfo">
        <div className="countryFlag" style={{ backgroundImage: `url(${flag})` }} />
        <div className="countryName">{countryName}</div>
      </div>
      <NudosMaskedNumberInput
        componentSize="small"
        label={`Costo de compra (${currencyCode})`}
        placeholder={`$0 ${currencyCode}`}
        currentValue={providerPrice}
        handleChange={inputText => handleChangeProviderPrice(inputText || '')}
        maskPrefix={'$'}
        maskSufix={` ${currencyCode}`}
        roundToClosest
        numberOfDecimals={2}
        errorText={!isProviderPriceValid ? ' ' : undefined}
      />
      <NudosMaskedNumberInput
        label={`Margen`}
        componentSize="extraSmall"
        placeholder={'0 %'}
        currentValue={`${margin || ''}`}
        handleChange={inputText => handleChangeMargin(inputText || '')}
        maskPrefix={''}
        maskSufix={'%'}
        roundToClosest
        numberOfDecimals={4}
        errorText={negativeOrZeroMarginAlert}
        inputPadding={12}
      />
      {pricingFields.map(field => {
        return (
          <PricingInputField
            key={field.dataName}
            componentSize={field.componentSize}
            dataName={field.dataName}
            label={field.label}
            initialValue={typeof field.initialValue === 'number' ? field.initialValue : 0}
            currentValue={typeof field.currentValue === 'number' ? field.currentValue : 0}
            suffixSymbol={field.suffixSymbol}
            placeholder={field.placeholder}
            isRequired={field.isRequired}
            isDisabled={field.isDisabled}
          />
        );
      })}
      <NudosMaskedNumberInput
        componentSize="small"
        label={getEcommercePriceSelectLabel(false)}
        placeholder={emptyFieldPlaceholder}
        isDeactivated={true}
        currentValue={nudosPriceUsd}
        maskPrefix={'$'}
        maskSufix={' USD'}
        roundToClosest
        numberOfDecimals={2}
      />
      <NudosMaskedNumberInput
        componentSize="small"
        label={getEcommercePriceSelectLabel(true)}
        placeholder={emptyFieldPlaceholder}
        isDeactivated={false}
        handleChange={inputText => handleChangeNudosPriceUsdWithTaxes(inputText || '')}
        currentValue={nudosPriceUsdWithTaxes}
        maskPrefix={'$'}
        maskSufix={' USD'}
        roundToClosest
        numberOfDecimals={2}
        errorText={!isNudosPriceUsdWithTaxesInputValid ? ' ' : undefined}
      />
      <div className="containerSearcher">
        <NudosDropdownWithSearchbar
          label="Proveedor"
          componentSize="mediumSmall"
          customClassLabel="customClassLabel"
          formDropdownOptionsCallback={formTaxDataDropdownOptionsFn(setNewProvider)}
          filterBySearchStringCallback={filterBySearchStringCallback}
          customPlaceholder="Proveedor"
          currentValue={newProvider}
          isFilled={!!newProvider}
          rawOptions={availableProviders || []}
        />
      </div>
      <div className="productStatusSwitch">
        {!productHasFullPricingDataForCountry && (
          <div className="mustCompleteDataHover">Para prenderlo debes llenar la información</div>
        )}
        {preloadedStatus === 'Sin stock' ? (
          <StatusButton status="outOfStockButton" />
        ) : (
          <div id="customSwitch">
            <Switch
              checked={productHasFullPricingDataForCountry ? activeCheck : false}
              onChange={handleClickSwitchActiveStatus}
              style={{ width: 'max-content', height: '24px' }}
              checkedChildren="Prendido"
              unCheckedChildren="Apagado"
            />
            <Popover
              trigger="click"
              placement="topRight"
              open={showActiveStatusPopover}
              content={confirmChangeActiveStatusPopoverContent}
            />
          </div>
        )}
      </div>
      <Form.Item name={`${countryNameCode}-isActive`} hidden />
      <Form.Item name={`${countryNameCode}-id`} hidden />
      <Form.Item name={`${countryNameCode}-name`} hidden />
      <Form.Item name={`${countryNameCode}-flag`} hidden />
      <Form.Item name={`${countryNameCode}-inStock`} hidden />
      <Form.Item name={`${countryNameCode}-providerPrice`} hidden />
      <Form.Item name={`${countryNameCode}-margin`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceUsd`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPrice`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceUsdWithTaxes`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceWithTaxes`} hidden />
      <Form.Item name={`${countryNameCode}-providerId`} hidden />
    </div>
  );
};

export default PricingDataByCountry;
